import { UseInfiniteQueryOptions, useMutation } from 'react-query'
import { getResources, getDataExplorerObject } from './dataExplorer'
import { DataExplorerResource, DataTreeResourceRequestProps } from 'features/new-data-explorer/types'
import { CartoApiError } from 'features/common'
import { deleteResourcePostRequest, getTilesetResourceStats } from 'features/data-explorer/api/dataExplorer'
import { Connection } from 'features/connections'
import { queryClient } from 'queryClient'

export const DATA_EXPLORER_KEYS = {
  backup: ['de-backup'] as const,
  backupTree: ['de-backup-tree'] as const,
  resources: ['resources'] as const,
  tilesetStats: ['tilesetStats'] as const,
  dataExplorerObject: ['dataExplorerObject'] as const
}

export const resourcesQuery = ({
  connectionId,
  fqn,
  search,
  searchFqnExact
}: DataTreeResourceRequestProps): UseInfiniteQueryOptions<DataExplorerResource, CartoApiError> => {
  const queryKey = [...DATA_EXPLORER_KEYS.resources, connectionId, fqn || null, search || null]
  return {
    queryKey,
    queryFn: ({ pageParam = 1 }) => getResources({ connectionId, fqn, search, page: pageParam, searchFqnExact }),
    enabled: false,
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage.page || 1
      const totalFetched = (currentPage - 1) * (lastPage.pageSize || 0) + (lastPage?.children?.length || 0)
      const hasMore = totalFetched < (lastPage.totalChildren || 0)
      return hasMore ? currentPage + 1 : undefined
    },
    staleTime: Infinity,
    retry: false
  }
}

export const dataExplorerObjectQuery = ({ link }: { link: string; signal?: AbortSignal }) => {
  const queryKey = [...DATA_EXPLORER_KEYS.dataExplorerObject, link]
  return {
    queryKey,
    queryFn: async ({ signal }: { signal?: AbortSignal }) => getDataExplorerObject({ link, signal })
  }
}

const tilesetStatsQuery = ({ connectionId, tilesetId }: { connectionId: string; tilesetId: string }) => {
  const queryKey = [...DATA_EXPLORER_KEYS.tilesetStats, connectionId, tilesetId]
  return {
    queryKey,
    queryFn: async () => getTilesetResourceStats({ connectionId, tilesetId })
  }
}

export function useDeleteResourceMutation() {
  return useMutation<unknown, Error, { connection: Connection; source: string }>({
    mutationFn: async ({ connection, source }) => {
      await deleteResourcePostRequest({ connection, table: source })
    },
    onSuccess: (_, { connection, source }) => {
      const parentFqn = source.split('.').slice(0, -1).join('.')
      queryClient.refetchQueries(resourcesQuery({ connectionId: connection.id, fqn: parentFqn }))
    }
  })
}

function useDataExplorerQueries() {
  const queries = {
    getResources: resourcesQuery,
    getTilesetStats: tilesetStatsQuery,
    dataExplorerObject: dataExplorerObjectQuery
  }
  return { queries }
}

export default useDataExplorerQueries
